import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { IMAGES, bgStyle } from '@/config/images';
import {
  Search,
  Zap,
  Focus,
  Flame,
  Wind,
  Wrench,
  ChevronRight,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  X,
  Check,
  Target,
  Eye,
  Settings,
  Bot,
  Send
} from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '@/App';

// Category icons mapping
const categoryIcons = {
  power: Zap,
  alignment: Target,
  quality: Eye,
  safety: Flame,
  mechanical: Wrench,
  focus: Focus
};

const categoryColors = {
  power: { bg: 'from-yellow-500/50 to-yellow-600/30', border: 'border-yellow-500/50', text: 'text-yellow-400' },
  alignment: { bg: 'from-purple-500/50 to-purple-600/30', border: 'border-purple-500/50', text: 'text-purple-400' },
  quality: { bg: 'from-blue-500/50 to-blue-600/30', border: 'border-blue-500/50', text: 'text-blue-400' },
  safety: { bg: 'from-red-500/50 to-red-600/30', border: 'border-red-500/50', text: 'text-red-400' },
  mechanical: { bg: 'from-orange-500/50 to-orange-600/30', border: 'border-orange-500/50', text: 'text-orange-400' },
  focus: { bg: 'from-green-500/50 to-green-600/30', border: 'border-green-500/50', text: 'text-green-400' }
};

const LaserTroubleshooting = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');

  // Inline AI chat state
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSessionId, setAiSessionId] = useState(null);
  const aiEndRef = useRef(null);

  // Fetch machines for Ask AI context
  useEffect(() => {
    const fetchMachines = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/laser/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMachines(response.data || []);
        if (response.data?.length > 0) {
          setSelectedMachine(response.data[0].id);
        }
      } catch (error) {
        console.log('Could not fetch laser machines');
      }
    };
    fetchMachines();
  }, [token]);

  // Fetch issues from API
  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const response = await axios.get(`${API}/laser/troubleshooting`);
        setIssues(response.data || []);
      } catch (error) {
        console.error('Failed to load laser troubleshooting issues:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchIssues();
  }, []);

  // Reset AI chat when selected issue changes
  useEffect(() => {
    if (!selectedIssue) {
      setAiMessages([]);
      setAiSessionId(null);
      return;
    }
    const machine = machines.find(m => m.id === selectedMachine);
    const machineLabel = machine
      ? `${machine.name || machine.brand || 'your laser'} (${machine.laser_type?.toUpperCase()} ${machine.power_watts}W)`
      : null;
    const intro = machineLabel
      ? `I'm ready to help troubleshoot "${selectedIssue.issue_name}" on ${machineLabel}. Describe what you're observing and I'll give targeted advice.`
      : `I'm ready to help troubleshoot "${selectedIssue.issue_name}". Describe what you're seeing and I'll give targeted advice.`;
    setAiMessages([{ role: 'assistant', content: intro }]);
    setAiSessionId(null);
    setAiInput('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIssue]);

  const handleAiSend = async (e) => {
    e.preventDefault();
    if (!aiInput.trim() || aiLoading || !token) return;

    const isFirstUserMessage = !aiMessages.some(m => m.role === 'user');
    const messageToSend = isFirstUserMessage
      ? `Laser troubleshooting issue: "${selectedIssue.issue_name}"\nKnown causes: ${selectedIssue.causes.slice(0, 3).join('; ')}\n\nMy question: ${aiInput}`
      : aiInput;

    setAiMessages(prev => [...prev, { role: 'user', content: aiInput }]);
    setAiInput('');
    setAiLoading(true);

    try {
      const res = await axios.post(
        `${API}/laser/ai/chat`,
        { message: messageToSend, session_id: aiSessionId || undefined, machine_id: selectedMachine || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setAiSessionId(res.data.session_id);
      setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', content: 'Failed to get a response. Please try again.' }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Filter issues
  const filteredIssues = useMemo(() => {
    return issues.filter(issue => {
      const matchesSearch = !searchTerm || 
        issue.issue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.explanation.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || issue.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [issues, searchTerm, selectedCategory]);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'power', label: 'Power Issues' },
    { value: 'alignment', label: 'Alignment' },
    { value: 'quality', label: 'Cut/Engrave Quality' },
    { value: 'safety', label: 'Safety' },
    { value: 'mechanical', label: 'Mechanical' },
    { value: 'focus', label: 'Focus' }
  ];

  if (isLoading) {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center"
        style={bgStyle(IMAGES.LASER_TROUBLESHOOTING)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Wrench className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading troubleshooting guide...</p>
        </div>
      </div>
    );
  }

  // Issue Detail View
  if (selectedIssue) {
    const colors = categoryColors[selectedIssue.category] || categoryColors.quality;
    const CategoryIcon = categoryIcons[selectedIssue.category] || Wrench;
    
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="laser-troubleshooting-detail"
        style={bgStyle(IMAGES.LASER_TROUBLESHOOTING)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => setSelectedIssue(null)}
          className="mb-4 text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Issues
        </Button>

        {/* Issue Header */}
        <div className="mb-6">
          <Badge className="bg-zinc-800 text-zinc-300 mb-2 capitalize">{selectedIssue.category}</Badge>
          <h1 className="text-2xl font-bold text-white mb-2">{selectedIssue.issue_name}</h1>
          <p className="text-zinc-400">{selectedIssue.explanation}</p>
        </div>

        {/* Causes */}
        <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Possible Causes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {selectedIssue.causes.map((cause, idx) => (
                <li key={idx} className="flex items-start gap-2 text-zinc-300">
                  <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>{cause}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Solutions */}
        <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Solutions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {selectedIssue.solutions.map((solution, idx) => (
                <li key={idx} className="flex items-start gap-2 text-zinc-300">
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>{solution}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Prevention */}
        {selectedIssue.prevention && (
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                Prevention Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-300">{selectedIssue.prevention}</p>
            </CardContent>
          </Card>
        )}

        {/* Inline AI Chat Panel */}
        {token && (
          <Card className="bg-[#1a1a1a] border-[#3a3a3a]">
            <CardHeader className="pb-3">
              <CardTitle className="text-red-400 flex items-center gap-2 text-lg">
                <Bot className="w-5 h-5" />
                Ask AI about this issue
              </CardTitle>
              {machines.find(m => m.id === selectedMachine) && (
                <CardDescription className="text-zinc-500 text-sm">
                  Laser context: {(() => { const m = machines.find(x => x.id === selectedMachine); return `${m.name || m.brand || 'Laser'} (${m.laser_type?.toUpperCase()} ${m.power_watts}W)`; })()}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <ScrollArea className="h-[300px] rounded-lg border border-[#3a3a3a] bg-[#141414] p-3">
                <div className="space-y-3">
                  {aiMessages.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      {m.role === 'assistant' && (
                        <Bot className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div className={`text-sm leading-relaxed whitespace-pre-wrap rounded-lg px-3 py-2 max-w-[85%] ${
                        m.role === 'user'
                          ? 'bg-red-900/20 border border-red-800/30 text-zinc-200'
                          : 'text-red-300'
                      }`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {aiLoading && (
                    <div className="flex gap-2">
                      <Bot className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-red-400 animate-pulse">Thinking…</div>
                    </div>
                  )}
                  <div ref={aiEndRef} />
                </div>
              </ScrollArea>
              <form onSubmit={handleAiSend} className="flex gap-2">
                <Input
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  placeholder="Describe what you're seeing…"
                  className="bg-[#1f1f1f] border-[#3a3a3a] text-white placeholder:text-zinc-500 flex-1"
                  disabled={aiLoading}
                />
                <Button
                  type="submit"
                  disabled={aiLoading || !aiInput.trim()}
                  className="bg-red-600 hover:bg-red-500 text-white"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Main Issue List View
  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="laser-troubleshooting"
      style={bgStyle(IMAGES.LASER_TROUBLESHOOTING)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <AlertTriangle className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">DIAGNOSE & FIX</div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Laser Troubleshooting
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-1">Diagnose and fix common CO2 and diode laser issues</p>
          </div>
        </div>
      </div>

      {/* Safety Warning */}
      <div className="bg-black/70 backdrop-blur-sm border border-yellow-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Flame className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-300">
              <strong>Safety First:</strong> Always wear appropriate eye protection and ensure proper ventilation 
              when working with lasers. Never look directly at the laser beam or its reflection.
            </p>
          </div>
        </div>
      </div>

      {/* Machine selector for AI context */}
      {machines.length > 0 && (
        <div className="flex items-center gap-3 mb-6 bg-black/50 backdrop-blur-sm border border-zinc-700/50 rounded-lg p-3">
          <Bot className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-zinc-300 text-sm">Laser context for AI:</span>
          <Select value={selectedMachine} onValueChange={setSelectedMachine}>
            <SelectTrigger className="w-52 bg-zinc-800/50 border-zinc-700 text-white text-sm">
              <SelectValue placeholder="Select machine" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {machines.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} ({m.laser_type?.toUpperCase()})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-zinc-500 text-xs">Select an issue below to chat with AI</span>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search issues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-black/70 backdrop-blur-sm border-zinc-700/50 text-white placeholder:text-zinc-500"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full md:w-48 bg-zinc-800/50 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Issues Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredIssues.map((issue) => {
          const colors = categoryColors[issue.category] || categoryColors.quality;
          const CategoryIcon = categoryIcons[issue.category] || Wrench;
          
          return (
            <Card
              key={issue.id}
              className="bg-black/70 backdrop-blur-sm border border-zinc-700/50 hover:border-orange-500/50 transition-all cursor-pointer"
              onClick={() => setSelectedIssue(issue)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CategoryIcon className={`w-6 h-6 ${colors.text}`} />
                  <Badge className="bg-zinc-800 text-zinc-300 capitalize">{issue.category}</Badge>
                </div>
                <CardTitle className="text-lg text-white mt-2">{issue.issue_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-300 line-clamp-2 mb-3">{issue.explanation}</p>
                <div className="flex items-center gap-1 text-[#e67e22] text-sm">
                  <span>View solutions</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredIssues.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl text-zinc-400 mb-2">No issues found</h3>
          <p className="text-zinc-500">Try adjusting your search or category filter</p>
        </div>
      )}
    </div>
  );
};

export default LaserTroubleshooting;
